import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../api/api";

// ─── Constantes ───────────────────────────────────────────────────────────────
const SERVICE_LABELS = {
  aire_acondicionado: "Aire Acondicionado", albanileria: "Albañilería",
  arquitectura: "Arquitectura", belleza_personal: "Belleza Personal",
  carpinteria: "Carpintería", cerrajeria: "Cerrajería",
  control_plagas: "Control de Plagas", cuidado_mayores: "Cuidado de Mayores",
  cuidado_ninos: "Cuidado de Niños", decoradores: "Decoradores",
  electricista: "Electricista", entrenamiento: "Entrenamiento",
  gasista: "Gasista", herrero: "Herrero", infantiles: "Infantiles (Fiestas)",
  instalador_general: "Instalador General", jardinero: "Jardinero",
  mascotas: "Mascotas", mecanica_automotriz: "Mecánica Automotriz",
  mudanzas: "Mudanzas", piletas: "Piletas", pintores: "Pintores",
  plomeros: "Plomeros", seguridad: "Seguridad",
  servicio_domestico: "Servicio Doméstico", tapicero: "Tapicero",
  tecnologia: "Tecnología",
};

const SERVICE_ICONS = {
  aire_acondicionado: "❄️", albanileria: "🧱", arquitectura: "🏛️",
  belleza_personal: "💇", carpinteria: "🪵", cerrajeria: "🔑",
  control_plagas: "🐛", cuidado_mayores: "👴", cuidado_ninos: "👶",
  decoradores: "🎨", electricista: "⚡", entrenamiento: "💪",
  gasista: "🔥", herrero: "⚒️", infantiles: "🎉",
  instalador_general: "🔩", jardinero: "🌿", mascotas: "🐾",
  mecanica_automotriz: "🚗", mudanzas: "📦", piletas: "🏊",
  pintores: "🖌️", plomeros: "🔧", seguridad: "🔒",
  servicio_domestico: "🧺", tapicero: "🛋️", tecnologia: "💻",
};

const STATUS_CONFIG = {
  created:            { label: "Creada",              color: "#5c7a8a", bg: "#f0f5f8",  step: 0 },
  waiting_assignment: { label: "Buscando prestador",  color: "#b06a00", bg: "#fff3e0",  step: 1 },
  waiting_quotes:     { label: "Esperando presupuestos", color: "#b06a00", bg: "#fff3e0", step: 1 },
  quoted:             { label: "Presupuestos listos", color: "#163244", bg: "#e7f0f5",  step: 2 },
  assigned:           { label: "Prestador asignado",  color: "#1b7f4e", bg: "#e8f7ef",  step: 3 },
  in_progress:        { label: "En progreso",         color: "#1b7f4e", bg: "#e8f7ef",  step: 3 },
  completed:          { label: "Completada",          color: "#163244", bg: "#e7f0f5",  step: 4 },
  cancelled:          { label: "Cancelada",           color: "#c0392b", bg: "#fdecea",  step: -1 },
  expired:            { label: "Expirada",            color: "#7b5ea7", bg: "#f3eeff",  step: -1 },
};

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `hace ${Math.floor(diff / 86400)} días`;
  return new Date(dateStr).toLocaleDateString("es-AR");
}

// ─── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ status }) {
  const cfg   = STATUS_CONFIG[status];
  const step  = cfg?.step ?? 0;
  const steps = ["Creada", "En búsqueda", "Presupuestos", "Asignada", "Completada"];

  if (step < 0) return null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginTop: 12 }}>
      {steps.map((s, i) => {
        const done   = i < step;
        const active = i === step;
        const isLast = i === steps.length - 1;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: isLast ? 0 : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 20, height: 20, borderRadius: "50%",
                background: done ? "#E7A432" : active ? "#163244" : "#e0e9ef",
                border: `2px solid ${done ? "#E7A432" : active ? "#163244" : "#e0e9ef"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, color: "#fff", fontWeight: 800, flexShrink: 0,
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{
                fontSize: 9, fontWeight: active ? 700 : 400,
                color: active ? "#163244" : done ? "#E7A432" : "#aab8c2",
                whiteSpace: "nowrap",
              }}>
                {s}
              </span>
            </div>
            {!isLast && (
              <div style={{
                flex: 1, height: 2, marginBottom: 14,
                background: done ? "#E7A432" : "#e0e9ef",
                minWidth: 16,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Request Card ─────────────────────────────────────────────────────────────
function RequestCard({ req, onViewQuotes, onRate, onChat }) {
  const cfg     = STATUS_CONFIG[req.status] || STATUS_CONFIG.created;
  const icon    = SERVICE_ICONS[req.service_key] || "🛠️";
  const label   = SERVICE_LABELS[req.service_key] || req.service_key;
  const isQuoted    = req.status === "quoted";
  const isCompleted = req.status === "completed";
  const isCancelled = ["cancelled", "expired"].includes(req.status);
  const hasChat     = ["assigned", "in_progress", "completed"].includes(req.status);

  return (
    <div style={{
      background: "#fff", borderRadius: 14, marginBottom: 14,
      border: `2px solid ${isQuoted ? "#E7A432" : isCancelled ? "#f0dede" : "#e0e9ef"}`,
      boxShadow: isQuoted
        ? "0 4px 20px rgba(231,164,50,0.12)"
        : "0 2px 10px rgba(22,50,68,0.06)",
      overflow: "hidden",
      opacity: isCancelled ? 0.7 : 1,
    }}>
      {/* Alert strip para presupuestos listos */}
      {isQuoted && (
        <div style={{
          background: "#E7A432", padding: "5px 20px",
          fontSize: 11, fontWeight: 800, color: "#163244",
          letterSpacing: "0.06em",
        }}>
          ⭐ PRESUPUESTOS LISTOS — Revisalos ahora
        </div>
      )}

      <div style={{ padding: "18px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10, flexShrink: 0,
              background: isCancelled ? "#f0f5f8" : "#163244",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22,
            }}>
              {icon}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#163244" }}>{label}</div>
              <div style={{ fontSize: 12, color: "#8fa3b1", marginTop: 2 }}>
                {timeAgo(req.created_at)}
                {req.common_data?.ubicacion?.ciudad && ` · ${req.common_data.ubicacion.ciudad}`}
              </div>
            </div>
          </div>

          {/* Badge estado */}
          <span style={{
            padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            color: cfg.color, background: cfg.bg, whiteSpace: "nowrap",
          }}>
            {cfg.label.toUpperCase()}
          </span>
        </div>

        {/* Descripción */}
        {req.common_data?.descripcion && (
          <div style={{
            background: "#f7fafc", borderRadius: 8, padding: "9px 14px",
            fontSize: 13, color: "#5c7a8a", lineHeight: 1.6,
            borderLeft: "3px solid #cddae4", marginBottom: 10,
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {req.common_data.descripcion}
          </div>
        )}

        {/* Datos del prestador asignado */}
        {req.provider_name && ["assigned", "in_progress", "completed"].includes(req.status) && (
          <div style={{
            background: "#f7fafc", borderRadius: 8,
            padding: "10px 12px", marginBottom: 6,
            border: "1px solid #e0e9ef",
          }}>
            {/* Fila superior: avatar + nombre + badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                background: "#163244", display: "flex", alignItems: "center",
                justifyContent: "center", fontWeight: 800, fontSize: 12, color: "#E7A432",
              }}>
                {req.provider_name[0]}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 12, color: "#163244" }}>
                  {req.provider_name}
                </div>
                {req.provider_rating && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: "#E7A432", fontSize: 11 }}>
                      {"★".repeat(Math.round(parseFloat(req.provider_rating)))}
                      <span style={{ color: "#e0e9ef" }}>
                        {"★".repeat(5 - Math.round(parseFloat(req.provider_rating)))}
                      </span>
                    </span>
                    <span style={{ fontSize: 11, color: "#8fa3b1" }}>
                      {parseFloat(req.provider_rating).toFixed(1)} · {req.provider_jobs || 0} trabajos
                    </span>
                  </div>
                )}
              </div>
              <div style={{
                marginLeft: "auto", fontSize: 10, fontWeight: 700,
                color: "#1b7f4e", background: "#e8f7ef",
                padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
              }}>
                Prestador asignado
              </div>
            </div>

            {/* Contacto — solo cuando status = assigned o in_progress */}
            {["assigned", "in_progress"].includes(req.status) && (req.provider_phone || req.provider_email) && (
              <div style={{
                marginTop: 10, paddingTop: 10,
                borderTop: "1px solid #e0e9ef",
                display: "flex", flexWrap: "wrap", gap: 8,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#5c7a8a", width: "100%", marginBottom: 2 }}>
                  📞 Datos de contacto
                </div>
                {req.provider_phone && (
                  <a
                    href={`tel:${req.provider_phone}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 14px", borderRadius: 8,
                      background: "#163244", color: "#fff",
                      fontSize: 12, fontWeight: 700, textDecoration: "none",
                    }}
                  >
                    📱 {req.provider_phone}
                  </a>
                )}
                {req.provider_email && (
                  <a
                    href={`mailto:${req.provider_email}`}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 14px", borderRadius: 8,
                      border: "1.5px solid #163244", background: "#fff",
                      color: "#163244", fontSize: 12, fontWeight: 700, textDecoration: "none",
                    }}
                  >
                    ✉️ {req.provider_email}
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Progress bar */}
        <ProgressBar status={req.status} />

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          {isQuoted && (
            <button
              onClick={() => onViewQuotes(req.id)}
              style={{
                flex: 1, padding: "11px", borderRadius: 8, border: "none",
                background: "#163244", color: "#fff",
                fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                fontSize: 14, cursor: "pointer",
              }}
            >
              Ver presupuestos →
            </button>
          )}

          {/* Botón Chat */}
          {hasChat && (
            <button
              onClick={() => onChat(req.id)}
              style={{
                flex: 1, padding: "11px", borderRadius: 8,
                border: "2px solid #163244", background: "#fff",
                color: "#163244", fontFamily: "Montserrat, sans-serif",
                fontWeight: 700, fontSize: 14, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              💬 Chat con prestador
            </button>
          )}

          {isCompleted && !req.rated && (
            <button
              onClick={() => onRate(req.id)}
              style={{
                flex: 1, padding: "11px", borderRadius: 8,
                border: "2px solid #E7A432", background: "#fffbf2",
                color: "#163244", fontFamily: "Montserrat, sans-serif",
                fontWeight: 700, fontSize: 14, cursor: "pointer",
              }}
            >
              ⭐ Calificar prestador
            </button>
          )}
          {isCompleted && req.rated && (
            <div style={{
              flex: 1, padding: "10px", borderRadius: 8,
              background: "#e8f7ef", border: "1px solid #1b7f4e",
              color: "#1b7f4e", fontSize: 13, fontWeight: 600,
              textAlign: "center",
            }}>
              ✓ Ya calificaste este trabajo
            </div>
          )}
          {["waiting_assignment", "waiting_quotes"].includes(req.status) && (
            <div style={{
              flex: 1, padding: "10px 14px", borderRadius: 8,
              background: "#f7fafc", border: "1px solid #e0e9ef",
              color: "#8fa3b1", fontSize: 13, textAlign: "center",
            }}>
              ⏳ Estamos buscando prestadores disponibles...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ navigate }) {
  return (
    <div style={{
      textAlign: "center", padding: "60px 20px",
      background: "#fff", borderRadius: 14,
      border: "2px dashed #e0e9ef",
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🛠️</div>
      <div style={{ fontWeight: 800, fontSize: 18, color: "#163244", marginBottom: 8 }}>
        Todavía no hiciste ninguna solicitud
      </div>
      <div style={{ color: "#8fa3b1", fontSize: 14, marginBottom: 28 }}>
        Explorá los servicios disponibles y pedí tu primera solicitud.
      </div>
      <button
        onClick={() => navigate("/services")}
        style={{
          padding: "13px 32px", borderRadius: 10, background: "#163244",
          border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif",
          fontWeight: 700, fontSize: 15, cursor: "pointer",
        }}
      >
        Ver servicios →
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientRequestsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [requests,    setRequests]    = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError,   setLoadError]   = useState("");
  const [filter,      setFilter]      = useState("all");

  const fetchRequests = () => {
    setLoadingData(true); setLoadError("");
    api.get("/api/service-requests/my")
      .then(res => setRequests(res.data.requests || res.data || []))
      .catch(err => {
        console.error("Error cargando solicitudes:", err);
        setLoadError("No se pudieron cargar tus solicitudes.");
      })
      .finally(() => setLoadingData(false));
  };

  useEffect(() => {
    fetchRequests();
  }, [location.key]);

  const FILTERS = [
    { id: "all",       label: "Todas",      count: requests.length },
    { id: "active",    label: "Activas",    count: requests.filter(r => ["created","waiting_assignment","waiting_quotes","quoted","assigned","in_progress"].includes(r.status)).length },
    { id: "completed", label: "Completadas",count: requests.filter(r => r.status === "completed").length },
    { id: "cancelled", label: "Canceladas", count: requests.filter(r => ["cancelled","expired"].includes(r.status)).length },
  ];

  const filtered = requests.filter(r => {
    if (filter === "active")    return ["created","waiting_assignment","waiting_quotes","quoted","assigned","in_progress"].includes(r.status);
    if (filter === "completed") return r.status === "completed";
    if (filter === "cancelled") return ["cancelled","expired"].includes(r.status);
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (a.status === "quoted" && b.status !== "quoted") return -1;
    if (b.status === "quoted" && a.status !== "quoted") return  1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const pendingQuotes = requests.filter(r => r.status === "quoted").length;

  return (
    <>
      <div style={{ fontFamily: "Montserrat, sans-serif", maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#163244" }}>
              Mis solicitudes
            </h1>
            {pendingQuotes > 0 && (
              <span style={{
                background: "#E7A432", color: "#163244", borderRadius: 20,
                padding: "2px 10px", fontSize: 12, fontWeight: 800,
              }}>
                {pendingQuotes} con presupuestos
              </span>
            )}
          </div>
          <p style={{ margin: 0, color: "#8fa3b1", fontSize: 14 }}>
            Seguí el estado de cada solicitud y revisá los presupuestos recibidos.
          </p>
        </div>

        {/* Filters */}
        {!loadingData && !loadError && requests.length > 0 && (
          <div style={{
            display: "flex", gap: 6, marginBottom: 20,
            background: "#fff", borderRadius: 12, padding: 6,
            boxShadow: "0 2px 8px rgba(22,50,68,0.06)",
          }}>
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  flex: 1, padding: "9px 8px", borderRadius: 8, border: "none",
                  background: filter === f.id ? "#163244" : "transparent",
                  color: filter === f.id ? "#fff" : "#5c7a8a",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: filter === f.id ? 700 : 500,
                  fontSize: 13, cursor: "pointer", transition: "all 0.2s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                {f.label}
                <span style={{
                  background: filter === f.id ? "#E7A432" : "#e0e9ef",
                  color: filter === f.id ? "#163244" : "#5c7a8a",
                  borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 800,
                }}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Loading */}
        {loadingData && (
          <div style={{ textAlign: "center", padding: "60px", color: "#8fa3b1" }}>
            <span style={{
              display: "inline-block", width: 28, height: 28,
              border: "3px solid #cddae4", borderTopColor: "#163244",
              borderRadius: "50%", animation: "spin 0.7s linear infinite", marginBottom: 14,
            }} /><br />
            Cargando tus solicitudes...
          </div>
        )}

        {/* Error */}
        {loadError && !loadingData && (
          <div style={{
            textAlign: "center", padding: "40px", background: "#fff",
            borderRadius: 14, color: "#c0392b", fontSize: 14,
          }}>
            ⚠ {loadError}
            <br />
            <button
              onClick={() => window.location.reload()}
              style={{ marginTop: 12, padding: "8px 20px", borderRadius: 8, background: "#163244", border: "none", color: "#fff", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Empty */}
        {!loadingData && !loadError && requests.length === 0 && (
          <EmptyState navigate={navigate} />
        )}

        {/* Cards */}
        {!loadingData && !loadError && sorted.map(req => (
          <RequestCard
            key={req.id}
            req={req}
            onViewQuotes={id => navigate(`/mis-solicitudes/${id}/presupuestos`)}
            onRate={id => navigate(`/calificar/${id}`)}
            onChat={id => navigate(`/chat/${id}`)}
          />
        ))}

        {/* Sin resultados en filtro */}
        {!loadingData && !loadError && requests.length > 0 && sorted.length === 0 && (
          <div style={{
            textAlign: "center", padding: "40px", background: "#fff",
            borderRadius: 14, color: "#8fa3b1", fontSize: 14,
            border: "2px dashed #e0e9ef",
          }}>
            No hay solicitudes en esta categoría.
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
